
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import {
  Lead, PermissionCode, ProjectsPool, Task, User, BoxStatusType, AttachmentType,
} from '@/types';
import UTILS from '@/lib/UTILS';
import API from '@/lib/API';
import {
  currentUserHasPermission, getLeadCustomValueText, getLeadCustomValueDate,
} from '@/helpers';
import TaskActivityBox from '@/components/TaskActivityBox.vue';

moment.locale('it');

@Component({
  components: {
    TaskActivityBox,
  },
})

export default class EditCheckAccountGSEDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  @Prop(Number) readonly projectId!: number;
  @Prop(Object) readonly task: Task | undefined;

  dialog = false
  valid = false
  loading = false

  selectedStatus: 'completed' | 'error' = 'completed'

  mDate(date: Date | null | undefined): string | null { return date ? moment(date).format('L') : null; }

  update = {
    checkAccountGSE: 'todo',
    checkAccountGSELastUpdate: null,
    checkAccountGSEUpdater: null,
    usernameGSE: null,
    passwordGSE: null,
    checkContoEnergia: null,
    checkAccountGSENotes: null,
  } as {
    checkAccountGSE: BoxStatusType;
    checkAccountGSELastUpdate: Date | null;
    checkAccountGSEUpdater: string | null;
    usernameGSE: string | null;
    passwordGSE: string | null;
    checkContoEnergia: string | null;
    checkAccountGSENotes: string | null;
  }

  checkContoEnergiaValues = [
    {
      text: 'Da fare',
      value: 'todo',
    },
    {
      text: '1° conto',
      value: 'conto1',
    },
    {
      text: '2° conto',
      value: 'conto2',
    },
    {
      text: '3° conto',
      value: 'conto3',
    },
    {
      text: '4° conto',
      value: 'conto4',
    },
    {
      text: '5° conto',
      value: 'conto5',
    },
    {
      text: 'NO',
      value: 'no',
    },
  ]

  get selectedTask(): Task | null {
    // Se non è stato specificato un Task nelle Prop,
    // utilizza il Task selezionato memorizzato tramite Vuex.
    return this.task ?? this.$store.getters.selectedTask;
  }

  get checkAccountGSETooltip(): string | null | undefined {
    return this.update.checkAccountGSELastUpdate
      ? `${moment(this.update.checkAccountGSELastUpdate).format('DD/MM/YYYY HH:mm')} - ${this.update.checkAccountGSEUpdater}`
      : 'Nessuna modifica effettuata';
  }

  @Watch('value')
  async onValueChange(val: boolean) {
    console.log(`${this.constructor.name}: onValueChange(${val})`); // eslint-disable-line no-console

    if (val === true) {
      // Se è stato specificato nelle Props l'id di un progetto (Lead)...
      if (this.projectId) {
        // ... prima di proseguire lo seleziona
        await this.$store.dispatch('selectLead', { id: this.projectId });
      }
    }

    if (this.hasPermission('taskcm.checkAccountGSE.show')) {
      this.$set(this.update, 'checkAccountGSE', (getLeadCustomValueText(this.lead, 'checkAccountGSE') ?? 'todo') as BoxStatusType);
      this.$set(this.update, 'checkAccountGSELastUpdate', getLeadCustomValueDate(this.lead, 'checkAccountGSELastUpdate'));
      this.$set(this.update, 'checkAccountGSEUpdater', getLeadCustomValueText(this.lead, 'checkAccountGSEUpdater' ?? ''));
      this.$set(this.update, 'usernameGSE', getLeadCustomValueText(this.lead, 'usernameGSE' ?? ''));
      this.$set(this.update, 'passwordGSE', getLeadCustomValueText(this.lead, 'passwordGSE' ?? ''));
      this.$set(this.update, 'checkContoEnergia', getLeadCustomValueText(this.lead, 'checkContoEnergia' ?? ''));
      this.$set(this.update, 'checkAccountGSENotes', getLeadCustomValueText(this.lead, 'checkAccountGSENotes' ?? ''));
    }

    this.dialog = val;
  }
  @Watch('dialog')
  onDialogChange(val: boolean) {
    console.log(`${this.constructor.name}: onDialogChange(${val})`); // eslint-disable-line no-console

    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get pool(): ProjectsPool {
    return this.$store.getters.currentPool;
  }

  get currentLead() {
    return this.$store.getters.currentLead;
  }

  get lead() {
    return this.isFather
      ? this.currentLead
      : this.father;
  }

  get father(): Lead | null {
    const firstTask: Task = this.currentLead.Tasks[0];
    const { fatherLeadId } = firstTask;

    return fatherLeadId && fatherLeadId in this.pool
      ? this.pool[fatherLeadId]
      : null;
  }

  get isChild(): boolean {
    return ['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.currentLead?.type);
  }

  get isFather(): boolean {
    return !this.isChild;
  }

  getForm(): (Vue & { validate: () => boolean; reset: () => boolean }) | undefined {
    return this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };
  }

  get title(): string {
    return 'Check account GSE';
  }

  hasPermission(codename: PermissionCode) { return currentUserHasPermission(codename); }

  async getUserNameFromCustomValue(name: string): Promise<string | null> {
    const userId = UTILS.getCustomValueUserIdByName(this.lead, name) ?? null;
    const userName = userId ? await API.getUserName(userId) : null;
    return userName;
  }

  onCancel() {
    console.log(`${this.constructor.name}: onCancel()`); // eslint-disable-line no-console

    this.dialog = false;
  }

  updateBoxStatus(name: string, status: BoxStatusType) {
    console.log(`${this.constructor.name}: updateBoxStatus(${name}, ${status})`); // eslint-disable-line no-console

    // Se lo stato non esiste si ferma subito
    // if (name in this === false) { return; }

    // this[name] = status;

    if (name === 'checkAccountGSE') {
      this.update.checkAccountGSELastUpdate = new Date();
      this.update.checkAccountGSEUpdater = this.$store.getters.currentUser.name;
    }

    this.update[name] = status;
  }

  async updateAttachmentStatus(type: AttachmentType, status: BoxStatusType) {
    console.log(`${this.constructor.name}: updateAttachmentStatus(${type}, ${status})`); // eslint-disable-line no-console

    // Se lo stato non è uno di quelli gestiti, si ferma subito
    if (type in this === false) { return; }

    (this[type] as BoxStatusType) = status;

    const update = {} as { [key: string]: unknown };

    update[type] = status;

    await this.$store.dispatch('updateLead', {
      updateType: 'task',
      update: {
        task: this.selectedTask,
        ...update,
      },
    });

    this.dialog = false;
    setTimeout(() => {
      this.loading = false;
    }, 500);
  }

  async onUpdate() {
    console.log(`${this.constructor.name}: onUpdate()`); // eslint-disable-line no-console

    const form = this.getForm();

    if (form?.validate()) {
      this.loading = true;

      const update = {} as { [key: string]: unknown };

      update.checkAccountGSE = this.update.checkAccountGSE;
      update.checkAccountGSELastUpdate = this.update.checkAccountGSELastUpdate;
      update.checkAccountGSEUpdater = this.update.checkAccountGSEUpdater;
      update.usernameGSE = this.update.usernameGSE;
      update.passwordGSE = this.update.passwordGSE;
      update.checkContoEnergia = this.update.checkContoEnergia;
      update.checkAccountGSENotes = this.update.checkAccountGSENotes;

      await this.$store.dispatch('updateLead', {
        updateType: 'task',
        update: {
          task: this.selectedTask,
          ...update,
        },
      });

      this.dialog = false;
      setTimeout(() => {
        this.loading = false;
      }, 500);
    }
  }
}
